import { useState, useCallback, useEffect } from 'react';
import { api } from '@halo/web-lib';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldOff,
  X,
} from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

type BreakerState = 'open' | 'half-open';

interface Breaker {
  platform: string;
  storeId: string;
  state: BreakerState;
}

interface BreakersResponse {
  count: number;
  breakers: Breaker[];
}

/* ─── Toast ───────────────────────────────────── */

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

function ToastBanner({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────── */

function displayStore(storeId: string): string {
  return storeId === '_all' ? 'all stores' : storeId;
}

function StateBadge({ state }: { state: BreakerState }) {
  if (state === 'open') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        open
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      half-open
    </span>
  );
}

/* ─── Main Component ──────────────────────────── */

export function CircuitBreakersPage() {
  const [breakers, setBreakers] = useState<Breaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const { toast, showToast } = useToast();

  const fetchBreakers = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/ingestion/admin/circuit-breakers');
      if (res.success && res.data) {
        setBreakers((res.data as BreakersResponse).breakers ?? []);
        setLastRefresh(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBreakers();
    const interval = setInterval(fetchBreakers, 15_000);
    return () => clearInterval(interval);
  }, [fetchBreakers]);

  // Auto-refresh countdown
  const [refreshCountdown, setRefreshCountdown] = useState(15);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCountdown(Math.max(0, 15 - Math.floor((Date.now() - lastRefresh) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  async function handleReset(platform: string, storeId: string) {
    const label = `${platform} / ${displayStore(storeId)}`;
    if (!confirm(`Reset circuit breaker for ${label}?\n\nOnly reset after the underlying issue is fixed.`)) return;
    const key = `reset-${platform}-${storeId}`;
    setActing(key);
    try {
      const res = await api.post(
        `/api/v1/ingestion/admin/circuit-breakers/${encodeURIComponent(platform)}/${encodeURIComponent(storeId)}/reset`,
        {},
      );
      if (res.success) {
        showToast({ message: `Breaker reset for ${label}`, type: 'success' });
        await fetchBreakers();
      } else {
        console.error('Reset failed:', res);
        showToast({ message: `Reset failed for ${label}`, type: 'error' });
      }
    } catch (err) {
      console.error('Reset failed:', err);
      showToast({ message: `Reset failed for ${label}`, type: 'error' });
    } finally {
      setActing(null);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {toast && <ToastBanner message={toast.message} type={toast.type} onDismiss={() => showToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Circuit Breakers</h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Platform connections currently throttled or paused. Reset only after the underlying issue is fixed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {refreshCountdown}s
          </span>
          <button
            onClick={fetchBreakers}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {breakers.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">All breakers closed.</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">All platform connections are healthy.</div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Platform</th>
                <th className="text-left px-4 py-2 font-medium">Store</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {breakers.map((b) => {
                const key = `reset-${b.platform}-${b.storeId}`;
                const isActing = acting === key;
                const isAll = b.storeId === '_all';
                return (
                  <tr
                    key={`${b.platform}-${b.storeId}`}
                    className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-950/50"
                  >
                    <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{b.platform}</td>
                    <td className="px-4 py-2.5">
                      {isAll ? (
                        <span className="italic text-gray-500 dark:text-gray-400">all stores</span>
                      ) : (
                        <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{b.storeId}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <StateBadge state={b.state} />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => handleReset(b.platform, b.storeId)}
                        disabled={isActing}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                      >
                        {isActing ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldOff className="w-3 h-3" />}
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
